/**
 * Preset picker modal. Replaces `preset-messages.js::showPresetMessagesModal`.
 *
 * Lists the canned messages from `preset.service.PRESETS`; on select, opens
 * the send modal with the body pre-filled.
 */

import { createLogger } from '../../core/logger.js';
import { escape } from '../../shared/escape.js';
import { normalize as normalizePlate, format as formatPlate } from '../../shared/plate.js';
import { PRESETS } from './preset.service.js';
import * as sendView from './send.view.js';

const log = createLogger('preset.view');

const OVERLAY_ID = 'messagingPresetOverlay';

/**
 * Show the preset picker. When the user taps a preset, the picker closes and
 * the send modal opens with the body filled in.
 *
 * @param {string} toPlate
 */
export function open(toPlate) {
  const plate = normalizePlate(toPlate);
  if (!plate) return;

  const overlay = ensureModal();
  /** @type {HTMLElement} */ (overlay.querySelector('.preset-target-label')).textContent =
    `לרכב ${formatPlate(plate)}`;

  const list = /** @type {HTMLElement} */ (overlay.querySelector('.preset-list'));
  list.innerHTML = PRESETS.map((p) => `
    <button class="preset-item" data-preset-id="${escape(p.id)}" type="button">
      <span class="preset-icon">${p.icon ?? '💬'}</span>
      <span class="preset-text">
        <strong>${escape(p.label)}</strong>
        <span class="preset-body">${escape(p.body)}</span>
      </span>
    </button>
  `).join('') + `
    <button class="preset-item preset-custom" data-preset-id="__custom" type="button">
      <span class="preset-icon">✍️</span>
      <span class="preset-text"><strong>הודעה חופשית</strong></span>
    </button>
  `;

  list.querySelectorAll('.preset-item').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-preset-id') ?? '';
      close();
      if (id === '__custom') {
        sendView.open(plate);
        return;
      }
      const preset = PRESETS.find((p) => p.id === id);
      if (!preset) {
        log.warn('preset not found:', id);
        return;
      }
      sendView.open(plate, { prefillBody: preset.body, presetId: preset.id });
    });
  });

  openOverlay(overlay);
}

export function close() {
  const overlay = document.getElementById(OVERLAY_ID);
  if (overlay) closeOverlay(overlay);
}

// ====================================================================
// internals
// ====================================================================

let stylesInjected = false;

function ensureModal() {
  let overlay = document.getElementById(OVERLAY_ID);
  if (overlay) return overlay;
  overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.className = 'drawer-overlay js-modal';
  overlay.setAttribute('aria-hidden', 'true');
  overlay.innerHTML = `
    <aside class="drawer" role="dialog" aria-modal="true" aria-labelledby="presetTitle">
      <header class="drawer-header">
        <h3 id="presetTitle">בחר הודעה מהירה</h3>
        <button class="drawer-close" data-close aria-label="סגור">✕</button>
      </header>
      <div class="drawer-body">
        <section class="drawer-content">
          <div class="preset-target-label" style="color:#6b7a89;font-size:13px;margin-bottom:10px;"></div>
          <div class="preset-list" style="display:flex;flex-direction:column;gap:8px;"></div>
        </section>
      </div>
      <footer class="drawer-footer">
        <button class="drawer-close-btn" data-close>סגור</button>
      </footer>
    </aside>
  `;
  document.body.appendChild(overlay);
  injectStyles();
  overlay.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', close));
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  return overlay;
}

function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    #${OVERLAY_ID} .preset-item {
      display: flex; align-items: center; gap: 12px; padding: 12px 14px;
      background: #fff; border: 1px solid #e2e8f0; border-radius: 12px;
      cursor: pointer; transition: background .12s; text-align: right;
      font: inherit; width: 100%;
    }
    #${OVERLAY_ID} .preset-item:hover { background: #f1f5f9; }
    #${OVERLAY_ID} .preset-icon { font-size: 22px; }
    #${OVERLAY_ID} .preset-text { display: flex; flex-direction: column; gap: 2px; }
    #${OVERLAY_ID} .preset-body { color: #6b7a89; font-size: 13px; }
    #${OVERLAY_ID} .preset-custom { border-style: dashed; }
  `;
  document.head.appendChild(style);
}

/** @param {HTMLElement} overlay */
function openOverlay(overlay) {
  overlay.setAttribute('aria-hidden', 'false');
  overlay.classList.add('open');
  overlay.style.display = 'flex';
  document.body.classList.add('no-scroll');
}

/** @param {HTMLElement} overlay */
function closeOverlay(overlay) {
  overlay.setAttribute('aria-hidden', 'true');
  overlay.classList.remove('open');
  setTimeout(() => {
    if (overlay.getAttribute('aria-hidden') === 'true') overlay.style.display = 'none';
  }, 300);
  document.body.classList.remove('no-scroll');
}
